import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router-dom";
import "./i18n";
import { ThemeProvider, StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";

import "primereact/resources/themes/lara-light-purple/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./locale-config";

import { GlobalStyles } from "./styles/globalStyles";
import { theme } from "./styles/theme";
import AppErrorBoundary from "./components/AppErrorBoundary";
import Layout from "./components/Layout/Layout";
import PrivateRoute from "./routes/PrivateRoute";
import ReferralHandler from "./handlers/ReferralHandler";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserProvider } from "./context/UserContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ViewportProvider } from "./context/ViewportContext";

import LoginPage from "./pages/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LidFormPage from "./pages/LidFormPage/LidFormPage";
import CoursesPage from "./pages/CoursesPage";
import LessonsPage from "./pages/LessonsPage";
import StudentsPage from "./pages/StudentsPage";
import ReferralPage from "./pages/ReferralPage/ReferralPage";
import SettingsPage from "./pages/SettingsPage/SettingsPage";
import PaymentPage from "./pages/PaymentPage/PaymentPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import SupportRequestPage from "./pages/SupportRequestPage/SupportRequestPage";
import TemplatesPage from "./pages/TemplatesPage/TemplatesPage";
import SupportResponsePage from "./pages/SupportResponsePage";
import AssignmentsPage from "./pages/AssignmentsPage/AssignmentsPage";
import StatisticsPage from "./pages/StatisticsPage/StatisticsPage";
import AssignmentResponsesPage from "./pages/AssignmentResponsesPage/AssignmentResponsesPage";
import AssignmentResponseDetailPage from "./pages/AssignmentResponseDetailPage/AssignmentResponseDetailPage";
import LeadsPage from "./pages/LeadsPage/LeadsPage";
import ModulesPage from "./pages/TemplatesPage/ModulesPage/ModulesPage";
import TestsPage from "./pages/TemplatesPage/TestsPage/TestsPage";
import PracticesPage from "./pages/TemplatesPage/PracticesPage/PracticesPage";
import ThemesPage from "./pages/TemplatesPage/ModulesPage/ThemesPage/ThemesPage";
import TimeTablesPage from "./pages/TemplatesPage/ModulesPage/TimeTablesPage/TimeTablesPage";
import EventsPage from "./pages/EventsPage/EventsPage";
import DocumentVersionsPage from "./pages/DocumentVersionsPage/DocumentVersionsPage";
import StudentPage from "./pages/TemplatesPage/StudentPage/StudentPage";
import StudentLogsPage from "./pages/StudentLogsPage/StudentLogsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import StudentLayout from "./pages/StudentPortal/StudentLayout";
import StudentCoursesPage from "./pages/StudentPortal/StudentCoursesPage";
import StudentCatalogPage from "./pages/StudentPortal/StudentCatalogPage";
import StudentCoursePage from "./pages/StudentPortal/StudentCoursePage";
import StudentLessonPage from "./pages/StudentPortal/StudentLessonPage";
import StudentAssignmentPage from "./pages/StudentPortal/StudentAssignmentPage";
import StudentCalendarPage from "./pages/StudentPortal/StudentCalendarPage";
import StudentCatalogLinkPage from "./pages/StudentPortal/StudentCatalogLinkPage";
import StudentLinkPage from "./pages/StudentPortal/StudentLinkPage";
import StudentTeachersPage from "./pages/StudentPortal/StudentTeachersPage";
import StudentPreferencesPage from "./pages/StudentPortal/StudentPreferencesPage";

const RoleHomeRedirect = () => {
  const { isAuthenticated, role } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={role === "student" ? "/student/courses" : "/courses"} replace />;
};

const LegacyStudentTeacherRedirect = () => {
  const { teacherId = "" } = useParams();
  return <Navigate to={`/student/catalog/${teacherId}`} replace />;
};

export const appShouldForwardProp = (propName: string, target: unknown): boolean =>
  typeof target === "string" ? isPropValid(propName) : true;

const App: React.FC = () => (
  <ViewportProvider>
    <StyleSheetManager shouldForwardProp={appShouldForwardProp}>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <AppErrorBoundary>
          <NotificationProvider>
            <AuthProvider>
              <UserProvider>
                <Router>
                  <ReferralHandler />
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<RegistrationPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/lead-create" element={<LidFormPage />} />
                    <Route path="/s/:slug" element={<StudentCatalogLinkPage />} />
                    <Route path="/student/link/:token" element={<StudentLinkPage />} />

                    <Route element={<PrivateRoute role="teacher" />}>
                      <Route element={<Layout />}>
                        <Route path="/courses" element={<CoursesPage viewMode="lessons" />} />
                        <Route path="/courses/:courseId/lessons" element={<LessonsPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        <Route path="/students" element={<StudentsPage />} />
                        <Route path="/students/:studentId/courses/:courseId/logs" element={<StudentLogsPage />} />
                        <Route path="/referals" element={<ReferralPage />} />
                        <Route path="/homework" element={<AssignmentResponsesPage />} />
                        <Route path="/assignment-responses/:id" element={<AssignmentResponseDetailPage />} />
                        <Route path="/payment" element={<PaymentPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/support-requests" element={<SupportRequestPage />} />
                        <Route path="/support-response/:id" element={<SupportResponsePage />} />
                        <Route path="/courses/:courseId/lessons/:lessonId/assignments" element={<AssignmentsPage />} />
                        <Route path="/documents" element={<TemplatesPage />} />
                        <Route path="/statistics" element={<StatisticsPage />} />
                        <Route path="/leads" element={<LeadsPage />} />
                        <Route path="/events" element={<EventsPage />} />
                        <Route path="/versions/:documentId/modules" element={<ModulesPage />} />
                        <Route path="/versions/:documentId/tests" element={<TestsPage />} />
                        <Route path="/versions/:documentId/practices" element={<PracticesPage />} />
                        <Route path="/versions/:documentId/students" element={<StudentPage />} />
                        <Route path="/modules/:documentId/:moduleId/themes" element={<ThemesPage />} />
                        <Route path="/modules/:documentId/:moduleId/timetables" element={<TimeTablesPage />} />
                        <Route path="/documents/:templateId/versions" element={<DocumentVersionsPage />} />
                      </Route>
                    </Route>

                    <Route element={<PrivateRoute role="student" />}>
                      <Route element={<StudentLayout />}>
                        <Route path="/student/courses" element={<StudentCoursesPage />} />
                        <Route path="/student/catalog" element={<StudentTeachersPage />} />
                        <Route path="/student/catalog/:teacherId" element={<StudentCatalogPage />} />
                        <Route path="/student/teachers" element={<Navigate to="/student/catalog" replace />} />
                        <Route path="/student/teachers/:teacherId" element={<LegacyStudentTeacherRedirect />} />
                        <Route path="/student/courses/:courseId" element={<StudentCoursePage />} />
                        <Route path="/student/courses/:courseId/lessons/:lessonId" element={<StudentLessonPage />} />
                        <Route path="/student/assignments/:assignmentId" element={<StudentAssignmentPage />} />
                        <Route path="/student/calendar" element={<StudentCalendarPage />} />
                        <Route path="/student/settings" element={<StudentPreferencesPage />} />
                      </Route>
                    </Route>

                    <Route path="/" element={<RoleHomeRedirect />} />
                    <Route path="/main" element={<RoleHomeRedirect />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Router>
              </UserProvider>
            </AuthProvider>
          </NotificationProvider>
        </AppErrorBoundary>
      </ThemeProvider>
    </StyleSheetManager>
  </ViewportProvider>
);

export default App;
